import {
    ArrowLeftIcon,
    BookOpenIcon,
    CalendarDaysIcon,
    ChatBubbleLeftRightIcon,
    ChevronRightIcon,
    ClipboardDocumentListIcon,
    ClockIcon,
    MapIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { BASE } from './_Layout'
import FamilyNotesSection from './FamilyNotesSection'
import InfoTip from './InfoTip'
import { ClientStatus, EngagementLevel, getFamilyDetail, MockFamilyDetail, MockHomeworkItem } from './mockData'

function formatDate(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const STATUS_BADGE: Record<ClientStatus, { label: string; cls: string }> = {
    active: { label: 'Active', cls: 'bg-green-50 text-green-700 border-green-200' },
    onboarding: { label: 'Onboarding', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    at_risk: { label: 'At risk', cls: 'bg-red-50 text-red-700 border-red-200' },
}

const ENGAGEMENT_LABEL: Record<EngagementLevel, string> = { high: 'High', medium: 'Medium', low: 'Low' }

export default function ProviderFamilyDetail() {
    const { id } = useParams()
    const detail = getFamilyDetail(id ?? '')
    const [showHomework, setShowHomework] = useState(false)

    if (!detail) {
        return (
            <div className='max-w-3xl mx-auto px-4 py-12 text-center'>
                <p className='text-neutral-600'>Client not found.</p>
                <Link to={`${BASE}/clients`} className='text-brand-600 hover:underline'>
                    Back to clients
                </Link>
            </div>
        )
    }

    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to={`${BASE}/clients`}
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                All clients
            </Link>

            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6'>
                <div>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>
                        {detail.childName ?? detail.email}
                    </h1>
                    <p className='text-sm text-neutral-600 mt-1'>
                        {detail.parentName ? `Parent ${detail.parentName} · ` : ''}
                        {detail.email}
                    </p>
                </div>
                <div className='flex items-center gap-2'>
                    <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${STATUS_BADGE[detail.status].cls}`}
                    >
                        {STATUS_BADGE[detail.status].label}
                        <InfoTip text='Active: engaging regularly. Onboarding: still completing setup and early milestones. At risk: no meaningful app activity in 30+ days, stalled roadmap progress, or incomplete onboarding.' />
                    </span>
                    <span className='inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border border-neutral-200 bg-neutral-50 text-neutral-700'>
                        {ENGAGEMENT_LABEL[detail.engagementLevel]} engagement
                        <InfoTip text='A High / Medium / Low rating from recent app activity (logins, roadmap steps, homework, saved resources, and chat) relative to the rest of the caseload.' />
                    </span>
                </div>
            </div>

            {/* Appointments */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6'>
                <Appointment
                    label='Last appointment'
                    value={formatDate(detail.lastAppointment) ?? 'No record'}
                    emphasis={false}
                />
                <Appointment
                    label='Upcoming appointment'
                    value={formatDate(detail.upcomingAppointment) ?? 'None scheduled'}
                    emphasis={!detail.upcomingAppointment}
                />
            </div>

            {detail.engagement && (
                <>
                    {/* Engagement KPIs */}
                    <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
                        <Stat
                            icon={ClockIcon}
                            label='Last active'
                            value={formatDate(detail.lastActiveAt) ?? 'Never'}
                            sub='in the client app'
                        />
                        <Stat
                            icon={ChatBubbleLeftRightIcon}
                            label='Chat messages this week'
                            value={String(detail.engagement.messagesThisWeek)}
                            sub={`${detail.engagement.messagesTotal} total`}
                        />
                        <Stat
                            icon={MapIcon}
                            label='Roadmap milestones'
                            value={String(detail.engagement.milestonesCompleted)}
                            sub='completed'
                        />
                        <Stat
                            icon={BookOpenIcon}
                            label='Resources saved'
                            value={String(detail.engagement.resourcesSaved)}
                            sub={`${detail.engagement.biteSizeStepsCompleted} bite-size steps done`}
                        />
                    </div>

                    {/* Homework — surfaced above the trend; highlighted when items are open */}
                    <button
                        onClick={() => setShowHomework(true)}
                        className={`w-full flex items-center gap-4 border rounded-2xl p-5 mb-6 text-left transition-colors ${
                            detail.openHomework > 0
                                ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
                                : 'bg-white border-neutral-200 hover:border-brand-300'
                        }`}
                    >
                        <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                detail.openHomework > 0 ? 'bg-amber-100' : 'bg-brand-50'
                            }`}
                        >
                            <ClipboardDocumentListIcon
                                className={`w-5 h-5 ${detail.openHomework > 0 ? 'text-amber-700' : 'text-brand-600'}`}
                            />
                        </div>
                        <div className='flex-1 min-w-0'>
                            <div className='flex items-center gap-2'>
                                <h2 className='text-base font-semibold text-neutral-900'>Homework</h2>
                                {detail.openHomework > 0 && (
                                    <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200'>
                                        {detail.openHomework} open
                                    </span>
                                )}
                            </div>
                            <p className='text-sm text-neutral-600'>
                                {detail.openHomework === 0
                                    ? 'No open homework. View homework history.'
                                    : `${detail.openHomework} open item${detail.openHomework === 1 ? '' : 's'} for the client to complete. View details.`}
                            </p>
                        </div>
                        <ChevronRightIcon className='w-5 h-5 text-neutral-400 flex-shrink-0' />
                    </button>

                    {/* Client engagement trend */}
                    <div className='bg-white border border-neutral-200 rounded-2xl p-6 mb-6'>
                        <h2 className='text-lg font-semibold text-neutral-900 mb-1'>Client engagement trend</h2>
                        <p className='text-sm text-neutral-600 mb-4'>
                            Weekly engagement across logins, roadmap steps, homework, saved resources, and chat. Message
                            content is never shared.
                        </p>
                        <TrendChart points={(detail.engagementByWeek ?? []).map((w) => w.score)} />
                    </div>
                </>
            )}

            {/* Coordinator notes are org-internal record-keeping. */}
            <FamilyNotesSection licenseId={id ?? ''} />

            {showHomework && <HomeworkModal detail={detail} onClose={() => setShowHomework(false)} />}
        </div>
    )
}

function HomeworkModal({ detail, onClose }: { detail: MockFamilyDetail; onClose: () => void }) {
    const items = detail.homework ?? []
    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50' onClick={onClose}>
            <div
                className='bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col'
                onClick={(e) => e.stopPropagation()}
            >
                <div className='flex items-center justify-between p-5 border-b border-neutral-100'>
                    <div>
                        <h2 className='text-lg font-semibold text-neutral-900'>Homework</h2>
                        <p className='text-sm text-neutral-500'>{detail.childName ?? detail.email}</p>
                    </div>
                    <button onClick={onClose} className='p-1 text-neutral-400 hover:text-neutral-700'>
                        <XMarkIcon className='w-5 h-5' />
                    </button>
                </div>
                <div className='p-5 overflow-y-auto space-y-3'>
                    {items.length === 0 ? (
                        <p className='text-sm text-neutral-500'>No homework has been assigned yet.</p>
                    ) : (
                        items.map((hw) => (
                            <div key={hw._id} className='border border-neutral-200 rounded-xl p-4'>
                                <div className='flex items-start justify-between gap-3'>
                                    <h3 className='text-sm font-semibold text-neutral-900'>{hw.title}</h3>
                                    <HomeworkStatusBadge status={hw.status} />
                                </div>
                                {hw.description && <p className='text-sm text-neutral-600 mt-1'>{hw.description}</p>}
                                <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 mt-2'>
                                    {hw.status === 'completed'
                                        ? hw.completedAt && <span>Completed {formatDate(hw.completedAt)}</span>
                                        : hw.dueDate && <span>Due {formatDate(hw.dueDate)}</span>}
                                    {hw.assignedByName && <span>Assigned by {hw.assignedByName}</span>}
                                </div>
                                {hw.completionNote && (
                                    <div className='mt-2 text-xs text-neutral-600 bg-neutral-50 border border-neutral-100 rounded-lg p-2'>
                                        &ldquo;{hw.completionNote}&rdquo;
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}

function HomeworkStatusBadge({ status }: { status: MockHomeworkItem['status'] }) {
    const map: Record<MockHomeworkItem['status'], { label: string; cls: string }> = {
        open: { label: 'Open', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
        overdue: { label: 'Overdue', cls: 'bg-red-50 text-red-700 border-red-200' },
        completed: { label: 'Completed', cls: 'bg-green-50 text-green-700 border-green-200' },
    }
    const s = map[status]
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${s.cls}`}
        >
            {s.label}
        </span>
    )
}

function Appointment({ label, value, emphasis }: { label: string; value: string; emphasis: boolean }) {
    return (
        <div
            className={`rounded-xl border p-4 ${emphasis ? 'border-amber-200 bg-amber-50' : 'border-neutral-200 bg-white'}`}
        >
            <div className='flex items-center gap-2 mb-2'>
                <CalendarDaysIcon className={`w-4 h-4 ${emphasis ? 'text-amber-600' : 'text-brand-600'}`} />
                <span className='text-xs uppercase tracking-wider text-neutral-500'>{label}</span>
            </div>
            <div className={`text-lg font-semibold ${emphasis ? 'text-amber-800' : 'text-neutral-900'}`}>{value}</div>
        </div>
    )
}

function Stat({
    icon: Icon,
    label,
    value,
    sub,
}: {
    icon: React.ComponentType<{ className?: string }>
    label: string
    value: string
    sub: string
}) {
    return (
        <div className='bg-white rounded-xl border border-neutral-200 p-4'>
            <div className='flex items-center gap-2 mb-2'>
                <Icon className='w-4 h-4 text-brand-600' />
                <span className='text-xs uppercase tracking-wider text-neutral-500'>{label}</span>
            </div>
            <div className='text-xl font-bold text-neutral-900'>{value}</div>
            <div className='text-xs text-neutral-500 mt-0.5'>{sub}</div>
        </div>
    )
}

function TrendChart({ points }: { points: number[] }) {
    if (points.length < 2) return <div className='text-sm text-neutral-500'>Not enough data yet.</div>
    const w = 600
    const h = 120
    const max = Math.max(...points, 1)
    const stepX = w / (points.length - 1 || 1)
    const path = points
        .map((p, i) => {
            const x = i * stepX
            const y = h - (p / max) * h
            return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
        })
        .join(' ')
    const area = `${path} L${w},${h} L0,${h} Z`
    return (
        <svg viewBox={`0 0 ${w} ${h}`} className='w-full h-32'>
            <defs>
                <linearGradient id='trend-fill' x1='0' x2='0' y1='0' y2='1'>
                    <stop offset='0%' stopColor='currentColor' stopOpacity='0.18' />
                    <stop offset='100%' stopColor='currentColor' stopOpacity='0' />
                </linearGradient>
            </defs>
            <g className='text-brand-600'>
                <path d={area} fill='url(#trend-fill)' />
                <path d={path} fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinejoin='round' strokeLinecap='round' />
                {points.map((p, i) => {
                    const x = i * stepX
                    const y = h - (p / max) * h
                    return <circle key={i} cx={x} cy={y} r='3' fill='currentColor' />
                })}
            </g>
        </svg>
    )
}
